<!--
  Fall Protection Plan Form
  
  Step-by-step plan submission with:
  - 3-5 steps: Basic Info, Fall Hazards, Fall Protections, Overhead Protection (conditional), Emergency Procedures (conditional)
  - Conditional step rendering based on employee training status
  - Multiple checkbox groups with validation requirements for safety compliance
  - Dynamic form validation with conditional field requirements
  - Signature requirements on final step
-->

<!-- Use the form layout component to handle the wizard UI -->
<app-form-layout
  [title]="formTitle"
  [form]="form"
  [icon]="formIcon"
  [disabled]="submitting"
  [onSubmit]="onSubmit.bind(this)"
  [steps]="steps"
  [currentStep]="currentStep()"
  [completedSteps]="completedSteps()"
  [canNavigateToStep]="canNavigateToStep.bind(this)"
  [isStepCompleted]="isStepCompleted.bind(this)"
  [onNextStep]="onNextStep.bind(this)"
  [onPreviousStep]="onPreviousStep.bind(this)"
  [getProgressPercentage]="getProgressPercentage.bind(this)"
>
  <div
    *ngIf="form && fieldMappings"
    formContent
    [formGroup]="form"
    class="form-content"
  >
    <!-- Basic Information Step -->
    <div *ngIf="currentStep() === steps[0].id" class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[0].title }}</h1>
            <p>{{ steps[0].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col>
              <app-date-input
                [templateFormGroup]="basicFormGroup"
                [control]="basicFormGroup.get('date')!"
                [label]="fieldMappings['date']"
                [requiredField]="requiredFields['project']"
                [today]="true"
             
              >
              </app-date-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="basicFormGroup"
                [control]="basicFormGroup.get('project')!"
                [requiredField]="requiredFields['project']"
                [label]="fieldMappings['project']"
                placeholder="Enter project name"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="basicFormGroup"
                [control]="basicFormGroup.get('jobsiteAddress')!"
                [label]="fieldMappings['jobsiteAddress']"
                [requiredField]="requiredFields['jobsiteAddress']"
                placeholder="Enter jobsite address"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Fall Hazards Step -->
    <div *ngIf="currentStep() === steps[1].id" class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[1].title }}</h1>
            <p>{{ steps[1].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col>
              <app-checkbox-input
                [templateFormGroup]="fallHazardsCheckboxesFormGroup"
                [checkboxes]="fallHazardsCheckboxes"
                [requiredField]="true"
                [label]="fieldMappings['fallHazards']"
           
              >
              </app-checkbox-input>
            </ion-col>
          </ion-row>
          <ion-row *ngIf="fallHazardsCheckboxesFormGroup.get('otherFallHazard')?.value">
            <ion-col>
              <app-text-input
                [templateFormGroup]="fallHazardsFormGroup"
                [control]="fallHazardsFormGroup.get('otherFallHazardSpecify')!"
                [label]="fieldMappings['otherFallHazardSpecify']"
                [requiredField]="fallHazardsCheckboxesFormGroup.get('otherFallHazard')?.value"
                placeholder="Specify other fall hazard"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Fall Protections Step -->
    <div *ngIf="currentStep() === steps[2].id" class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[2].title }}</h1>
            <p>{{ steps[2].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col>
              <app-checkbox-input
                [templateFormGroup]="fallRestraintCheckboxesFormGroup"
                [checkboxes]="fallRestraintCheckboxes"
                [label]="fieldMappings['fallRestraint']"
                [requiredField]="true"
              >
              </app-checkbox-input>
            </ion-col>
          </ion-row>
          <ion-row *ngIf="fallRestraintCheckboxesFormGroup.get('otherFallRestraint')?.value">
            <ion-col>
              <app-text-input
                [templateFormGroup]="fallProtectionsFormGroup"
                [control]="fallProtectionsFormGroup.get('otherFallRestraintSpecify')!"
                [label]="fieldMappings['otherFallRestraintSpecify']"
                [requiredField]="fallRestraintCheckboxesFormGroup.get('otherFallRestraint')?.value"
                placeholder="Specify other fall protection method"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-checkbox-input
                [templateFormGroup]="fallProtectionsFormGroup"
                [checkboxes]="articulatedLiftsCheckboxes"
                [label]="fieldMappings['articulatedLifts']"
                [requiredField]="false"
              >
              </app-checkbox-input>
            </ion-col>
          </ion-row>
          <ng-container *ngIf="fallProtectionsFormGroup.get('employeesTrained')?.value === 'No'">
            <ion-row>
              <ion-col>
                <ion-text color="danger">
                  <h2>
                    Employees not trained may not wear Fall Protection.
                  </h2>
                </ion-text>
              </ion-col>
            </ion-row>
                      </ng-container>
          <ion-row>
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="fallProtectionsFormGroup"
                [control]="fallProtectionsFormGroup.get('employeesTrained')!"
                [label]="fieldMappings['employeesTrained']"
                [requiredField]="requiredFields['employeesTrained']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
          <ng-container *ngIf="fallProtectionsFormGroup.get('employeesTrained')?.value === 'Yes'">
            <ion-row>
              <ion-col>
                <app-radio-group-input
                  [templateFormGroup]="fallProtectionsFormGroup"
                  [control]="fallProtectionsFormGroup.get('documentationAvailable')!"
                  [label]="fieldMappings['documentationAvailable']"
                  [requiredField]="requiredFields['documentationAvailable']"
                  [options]="['Yes', 'No']"
                  [orientation]="'horizontal'"
                >
                </app-radio-group-input>
              </ion-col>
            </ion-row>
            <ion-row *ngIf="fallProtectionsFormGroup.get('documentationAvailable')?.value === 'No'">
              <ion-col>
                <app-text-input
                  [templateFormGroup]="fallProtectionsFormGroup"
                  [control]="fallProtectionsFormGroup.get('alternateDocumentation')!"
                  [label]="fieldMappings['alternateDocumentation']"
                  [requiredField]="requiredFields['alternateDocumentation']"
                  placeholder="Specify alternate documentation"
                >
                </app-text-input>
              </ion-col>
            </ion-row>
          </ng-container>
  
        </ion-grid>
      </ion-list>
    </div>


   
    <!-- Overhead Protection Step (Conditional) -->
    <div *ngIf=" steps[3] && currentStep() === steps[3].id " class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[3].title }}</h1>
            <p>{{ steps[3].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
         
          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="overheadProtectionFormGroup"
                [requiredField]="requiredFields['assemblyDescription']"
                [control]="overheadProtectionFormGroup.get('assemblyDescription')!"
                [label]="fieldMappings['assemblyDescription']"
                [rows]="3"
                placeholder="Enter assembly description"
              >
              </app-textarea-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="overheadProtectionFormGroup"
                [control]="overheadProtectionFormGroup.get('maintenanceDescription')!"
                [requiredField]="requiredFields['maintenanceDescription']"
                [label]="fieldMappings['maintenanceDescription']"
                [rows]="3"
                placeholder="Enter maintenance description"
              >
              </app-textarea-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="overheadProtectionFormGroup"
                [control]="overheadProtectionFormGroup.get('responsiblePersonName')!"
                [label]="fieldMappings['responsiblePersonName']"
                [requiredField]="requiredFields['responsiblePersonName']" 
                placeholder="Enter responsible person name"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-checkbox-input
                [templateFormGroup]="materialHandlingCheckboxesFormGroup"
                [checkboxes]="materialHandlingCheckboxes"
                [label]="fieldMappings['materialHandling']"
                [requiredField]="true"
              >
              </app-checkbox-input>
            </ion-col>
          </ion-row>
          <ion-row *ngIf="materialHandlingCheckboxesFormGroup.get('otherMaterialHandling')?.value">
            <ion-col>
              <app-text-input
                [templateFormGroup]="overheadProtectionFormGroup"
                [control]="overheadProtectionFormGroup.get('otherMaterialHandlingSpecify')!"
                [label]="fieldMappings['otherMaterialHandlingSpecify']"
                [requiredField]="materialHandlingCheckboxesFormGroup.get('otherMaterialHandling')?.value"
                placeholder="Specify other material handling method"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-checkbox-input
                [templateFormGroup]="overheadProtectionCheckboxesFormGroup"
                [checkboxes]="overheadProtectionCheckboxes"
                [label]="fieldMappings['overheadProtection']"
                [requiredField]="true"
              >
              </app-checkbox-input>
            </ion-col>
          </ion-row>
          <ion-row *ngIf="overheadProtectionCheckboxesFormGroup.get('otherOverheadProtection')?.value">
            <ion-col>
              <app-text-input
                [templateFormGroup]="overheadProtectionFormGroup"
                [requiredField]="requiredFields['otherOverheadProtectionSpecify']"
                [control]="overheadProtectionFormGroup.get('otherOverheadProtectionSpecify')!"
                [label]="fieldMappings['otherOverheadProtectionSpecify']"
                [requiredField]="overheadProtectionCheckboxesFormGroup.get('otherOverheadProtection')?.value"
                placeholder="Specify other overhead protection measure"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Emergency Procedures Step (Conditional) -->
    <div *ngIf=" steps[4] && currentStep() === steps[4].id " class="step-content">
      <ion-list>
        <ion-item-divider class="form-step-title" >
          <ion-label>
            <h1>{{ steps[4].title }}</h1>
            <p>{{ steps[4].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col>
              <app-checkbox-input
                [templateFormGroup]="emergencyProceduresCheckboxesFormGroup"
                [checkboxes]="emergencyProceduresCheckboxes"
                [label]="fieldMappings['emergencyProcedures']"
                [requiredField]="true"
              >
              </app-checkbox-input>
            </ion-col>
          </ion-row>
          <ion-row *ngIf="emergencyProceduresCheckboxesFormGroup.get('otherEmergencyProcedure')?.value">
            <ion-col>
              <app-text-input
                [templateFormGroup]="emergencyProceduresFormGroup"
                [control]="emergencyProceduresFormGroup.get('otherEmergencyProcedureSpecify')!"
                [label]="fieldMappings['otherEmergencyProcedureSpecify']"
                [requiredField]="emergencyProceduresCheckboxesFormGroup.get('otherEmergencyProcedure')?.value"
                placeholder="Specify other emergency procedure"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="emergencyProceduresFormGroup"
                [control]="emergencyProceduresFormGroup.get('additionalSafetyMeasures')!"
                [label]="fieldMappings['additionalSafetyMeasures']"
                [requiredField]="requiredFields['additionalSafetyMeasures']"
                [rows]="3"
                placeholder="Enter additional safety measures"
              >
              </app-textarea-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col>
              <app-signature-section
              [signatureInfo]="formAttachmentRequirements!.signatures![0]!"
              ></app-signature-section>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>
  </div>
</app-form-layout> 